
























































































































































import { Component } from "vue-property-decorator";
import MiraBarRowComponent from "@/components/mira/MiraBarRow.vue";
import MiraDetailCardComponent from "@/components/mira/MiraDetailCard.vue";
import MiraDotPlotComponent from "@/components/mira/MiraDotPlot.vue";
import MiraFactsComponent from "@/components/mira/MiraFacts.vue";
import MiraKpiTileComponent from "@/components/mira/MiraKpiTile.vue";
import MiraKpiTileGridComponent from "@/components/mira/MiraKpiTileGrid.vue";
import MiraComparisonCardComponent from "@/components/mira/MiraComparisonCard.vue";
import MiraScreenBase from "@/components/mira/MiraScreenBase";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraSplitBarsComponent from "@/components/mira/MiraSplitBars.vue";
import MiraStackedBarComponent from "@/components/mira/MiraStackedBar.vue";
import MiraTrendCardComponent from "@/components/mira/MiraTrendCard.vue";
import MiraStackedTrendCardComponent from "@/components/mira/MiraStackedTrendCard.vue";
import MiraUnlockListComponent from "@/components/mira/MiraUnlockList.vue";
import { MiraSection } from "@/data/models/Mira";
import {
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
  COLOR_STANDARD_WHITE,
  COLOR_SURFACE_DARK,
} from "@/utils/colors";
import { MiraSectionUnlocks, collectUnlocks } from "@/utils/MiraDataQuality";
import { MiraBar, MiraSegment, MiraTile } from "@/utils/MiraDataQualityView";
import {
  MiraDotRow,
  MiraFact,
  MiraSplitRow,
  areaFacts,
  buildAreaSegments,
  buildDryingTiles,
  buildDryingTimeRows,
  buildMeasurementRows,
  hasMeasurementsPerProject,
  buildPauseRows,
  dryingTrendFormatter,
  forecastFacts,
  forecastMeter,
  formatDays,
  inPreparation,
  pauseLegend,
  shortChainsNote,
} from "@/utils/MiraDryingView";
import { themed } from "@/utils/Theme";

/**
 * Mira Trocknung: HyDry drying time and pause share without a project, the drying time and pause share
 * per finished project (#2610040), measurement frequency, drying area, and how far dryings run past their
 * forecast.
 *
 * Built like Datenqualität (`MiraScreenBase` for loading, the period picker and the header). Three
 * decisions shape what it shows, all enforced in `@/utils/MiraDryingView`: HyDry and conventional are
 * never set side by side while the HyDry benefit figure (DRY-004) is held back; changes are neutral;
 * the mean leads with the median beside it. A client without projects gets only the two HyDry figures,
 * because the others cannot apply to it. See `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-bar-row": MiraBarRowComponent,
    "mira-detail-card": MiraDetailCardComponent,
    "mira-dot-plot": MiraDotPlotComponent,
    "mira-facts": MiraFactsComponent,
    "mira-kpi-tile": MiraKpiTileComponent,
    "mira-kpi-tile-grid": MiraKpiTileGridComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-split-bars": MiraSplitBarsComponent,
    "mira-stacked-bar": MiraStackedBarComponent,
    "mira-comparison-card": MiraComparisonCardComponent,
    "mira-trend-card": MiraTrendCardComponent,
    "mira-stacked-trend-card": MiraStackedTrendCardComponent,
    "mira-unlock-list": MiraUnlockListComponent,
  },
})
export default class MiraDryingComponent extends MiraScreenBase {
  get section(): MiraSection {
    return MiraSection.HYDRY;
  }

  get titleKey(): string {
    return "mira.drying.title";
  }

  get trackColor(): string {
    return themed(COLOR_MIRA_BAR_TRACK, COLOR_MIRA_BAR_TRACK_DARK);
  }

  /** The card's surface, for the ring that keeps overlapping dot plot marks apart. */
  get surfaceColor(): string {
    return themed(COLOR_STANDARD_WHITE, COLOR_SURFACE_DARK);
  }

  get tiles(): MiraTile[] {
    return buildDryingTiles(this.period, this.comparison, this.viewContext);
  }

  get dryingTimeRows(): MiraDotRow[] {
    return buildDryingTimeRows(this.period, this.viewContext);
  }

  get shortChainsNote(): string | null {
    return shortChainsNote(this.period, this.viewContext);
  }

  get pauseRows(): MiraSplitRow[] {
    return buildPauseRows(this.period, this.viewContext);
  }

  get pauseLegend(): Array<{ label: string; color: string }> {
    return pauseLegend(this.viewContext);
  }

  get measurementRows(): MiraBar[] {
    return buildMeasurementRows(this.period, this.viewContext);
  }

  /** The measurement card's title: Zwischenmessungen je Projekt (#2610112 X5), or the older per-week figure. */
  get measurementCardKeys(): { title: string; subtitle: string } {
    return hasMeasurementsPerProject(this.period)
      ? { title: "mira.drying.cards.measurementsPerProject", subtitle: "mira.drying.cards.measurementsPerProjectSubtitle" }
      : { title: "mira.drying.cards.measurementFrequency", subtitle: "mira.drying.cards.measurementFrequencySubtitle" };
  }

  get areaSegments(): MiraSegment[] {
    return buildAreaSegments(this.period, this.viewContext);
  }

  get areaFacts(): MiraFact[] {
    return areaFacts(this.period, this.viewContext);
  }

  get forecastMeter(): MiraBar | null {
    return forecastMeter(this.period, this.viewContext);
  }

  get forecastFacts(): MiraFact[] {
    return forecastFacts(this.period, this.viewContext);
  }

  get inPreparation(): string[] {
    return inPreparation(this.period, this.viewContext);
  }

  /**
   * Only this section's figures waiting on data: the other sections have their own screens. Computed
   * here rather than through `super`, which vue-class-component's computed getters do not support.
   */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod).filter((group) => group.section === MiraSection.HYDRY);
  }

  formatAxisDays(days: number): string {
    return formatDays(days, this.viewContext);
  }

  trendFormatter(metricId: string): (value: number) => string {
    return dryingTrendFormatter(metricId, this.viewContext, this.period);
  }
}
